import { createSelector } from 'reselect';
import * as actions from '../actions/types';
import { combineReducers } from 'redux';
import uniqid from 'uniqid';
import areasBillboards from './areasBillboards';
import auth from './auth';
import checkoutData, { CHECKOUT_STEPS } from './checkoutData';
import entities from './entities';
import home from './home';
import ie from './ie';
import notifications from './notifications';
import now from './now';
import packages from './packages';
import posts from './posts';
import refundRequest from './refundRequest';
import showtimesData from './showtimesData';
import statesBillboards from './statesBillboards';
import ui from './ui';
import user from './user/index';
import wallet from './wallet';
import {filterVersionsForDate,filterVersionsByHours,filterAvailableVersions,getFilteredVersions} from '../utils/showtimes';
import {sortByProperty, unique} from '../utils/arrays';
import {getCashPaymentMethodsAllowed, getOptionLabel, parseOrderAmountByPaymentMethod} from '../utils/checkout';
import { compareIgnoringAccents } from '../utils/strings';
import landings from './landings';
import yearRecap from './yearRecap';
import { isUserCouponsLoading, isUserCouponsProcessing } from './user/coupons';
import { CHECKOUT_CONTEXT_CANDYBAR, CHECKOUT_CONTEXT_LOYALTY, CHECKOUT_CONTEXT_LOYALTY_PACKAGES, CHECKOUT_CONTEXT_TICKETS, CHECKOUT_CONTEXT_SIGN_UP, CHECKOUT_CONTEXT_GIFT_CARD } from '../actions/creators/checkout';
import { getCandybarItemPriceWithConfigs, getCandybarItemPriceWithSelectedOptions } from '../utils/candybar';
import { centsToCurrencyString } from '../utils/numbers';
import { isWalletCardAdding, isWalletCardPinRequesting, isWalletCardRemoving } from './user/wallet-cards';

const configData = window.appConfig;
const config = (state = configData, action) => {
  switch (action.type) {
    case actions.CONFIG_FETCH_SUCCESS:
      return action.config;
    default:
      return state;
  }
};

const defaultMovieTabState = {
  hidden: true,
};
const movieTab = (state = defaultMovieTabState, action) => {
  switch (action.type) {
    case actions.MOVIE_TAB_HIDE_TOGGLE:
      return Object.assign({}, state, {hidden: !state.hidden});
    case actions.MOVIE_TAB_OPEN:
    case actions.MOVIE_UPCOMING_SELECT:
    case actions.MOVIE_SELECT:
      return Object.assign({}, state, {hidden: false});
    case actions.MOVIE_TAB_CLOSE:
      return Object.assign({}, state, {hidden: true});
    default:
      return state;
  }
};

const dialogs = (state = {}, action) => {
  switch (action.type) {
    case actions.DIALOG_DISPLAY:
      return Object.assign({}, state, createDialog(action.payload));
    case actions.DIALOG_CLOSE:
      const result = Object.assign({}, state);
      delete result[action.id];
      return result;
    default:
      return state;
  }
};

const createDialog = dialog => {
  const id = uniqid();

  return {[id]: Object.assign({}, dialog, {id})};
};

const cinemasBillboards = (state = {}, action) => {
  switch (action.type) {
    case actions.CINEMA_MOVIES_FETCH_SUCCESS:
      return Object.assign({}, state, {[action.cinemaId]: action.movies});
    default:
      return state;
  }
};

const contactForm = (state = {isProcessing: false, isSubmitted: false}, action) => {
  switch (action.type) {
    case actions.CONTACT_FORM_SHOW:
      return Object.assign({}, state, {isProcessing: false, isSubmitted: false});
    case actions.CONTACT_FORM_SUBMIT:
      return Object.assign({}, state, {isProcessing: true, isSubmitted: false});
    case actions.CONTACT_FORM_SUCCESS:
      return Object.assign({}, state, {isProcessing: false, isSubmitted: true});
    case actions.CONTACT_FORM_ERROR:
      return Object.assign({}, state, {isProcessing: false, isSubmitted: false});
    default:
      return state;
  }
};

const jobsForm = (state = {isProcessing: false, isSubmitted: false}, action) => {
  switch (action.type) {
    case actions.JOBS_FORM_SHOW:
      return Object.assign({}, state, {isProcessing: false, isSubmitted: false});
    case actions.JOBS_FORM_SUBMIT:
      return Object.assign({}, state, {isProcessing: true, isSubmitted: false});
    case actions.JOBS_FORM_SUCCESS:
      return Object.assign({}, state, {isProcessing: false, isSubmitted: true});
    case actions.JOBS_FORM_ERROR:
      return Object.assign({}, state, {isProcessing: false, isSubmitted: false});
    default:
      return state;
  }
};

const arcoForm = (state = {isProcessing: false, isSubmitted: false}, action) => {
  switch (action.type) {
    case actions.ARCO_FORM_SHOW:
      return Object.assign({}, state, {isProcessing: false, isSubmitted: false, ref: null});
    case actions.ARCO_FORM_SUBMIT:
      return Object.assign({}, state, {isProcessing: true, isSubmitted: false, ref: null});
    case actions.ARCO_FORM_SUCCESS:
      return Object.assign({}, state, {isProcessing: false, isSubmitted: true, ref: action.data.ref});
    case actions.ARCO_FORM_ERROR:
      return Object.assign({}, state, {isProcessing: false, isSubmitted: false, ref: null});
    default:
      return state;
  }
};

const corpSalesForm = (state = {isProcessing: false, isSubmitted: false}, action) => {
  switch (action.type) {
    case actions.CORP_SALES_FORM_SHOW:
      return Object.assign({}, state, {isProcessing: false, isSubmitted: false});
    case actions.CORP_SALES_FORM_SUBMIT:
      return Object.assign({}, state, {isProcessing: true, isSubmitted: false});
    case actions.CORP_SALES_FORM_SUCCESS:
      return Object.assign({}, state, {isProcessing: false, isSubmitted: true});
    case actions.CORP_SALES_FORM_ERROR:
      return Object.assign({}, state, {isProcessing: false, isSubmitted: false});
    default:
      return state;
  }
};

const fingerprint = (state = null, action) => {
  switch (action.type) {
    case actions.FINGERPRINT_SET:
      return action.id;
    default:
      return state;
  }
}

const news = (state = {ids: [], lastLoadTime: null, isLoading: false}, action) => {
  switch (action.type) {
    case actions.NEWS_FETCH:
      return Object.assign({}, state, {isLoading: true});
    case actions.NEWS_FETCH_SUCCESS:
      return Object.assign({}, state, {ids: action.data.map(article => article.id), lastLoadTime: new Date().getTime(), isLoading: false});
    case actions.NEWS_FETCH_ERROR:
      return Object.assign({}, state, {isLoading: false});
    default:
      return state;
  }
};

const ieBenefits = (state = {categories: {}, lastLoadTime: null, isLoading: false}, action) => {
  switch (action.type) {
    case actions.IE_BENEFITS_FETCH:
      return Object.assign({}, state, {isLoading: true});
    case actions.IE_BENEFITS_FETCH_SUCCESS:
      return Object.assign({}, state, {categories: parseIeBenefitsData(action.data), lastLoadTime: new Date().getTime(), isLoading: false});
    case actions.IE_BENEFITS_FETCH_ERROR:
      return Object.assign({}, state, {isLoading: false});
    default:
      return state;
  }
};

const parseIeBenefitsData = data => {
  const result = {};
  Object.keys(data).forEach(category => {
    result[category] = data[category].map(benefit => benefit.id);
  });

  return result;
};

const promos = (state = {lastLoadTime: null, isLoading: false}, action) => {
  switch (action.type) {
    case actions.PROMOS_FETCH:
      return Object.assign({}, state, {isLoading: true});
    case actions.PROMOS_FETCH_SUCCESS:
      return Object.assign({}, state, {lastLoadTime: new Date().getTime(), isLoading: false});
    case actions.PROMOS_FETCH_ERROR:
      return Object.assign({}, state, {isLoading: false});
    default:
      return state;
  }
};

export default combineReducers({
  arcoForm,
  areasBillboards,
  auth,
  checkoutData,
  cinemasBillboards,
  config,
  contactForm,
  corpSalesForm,
  dialogs,
  entities,
  fingerprint,
  home,
  ie,
  ieBenefits,
  jobsForm,
  movieTab,
  news,
  notifications,
  landings,
  now,
  packages,
  posts,
  promos,
  refundRequest,
  showtimesData,
  statesBillboards,
  ui,
  user,
  wallet,
  yearRecap
});

// Entities
export function getCinemasById (state) {
  return state.entities.cinemas;
}
export const getCinemaEntities = getCinemasById;
export const getTicketsEntities = state => state.entities.tickets;
const getModalsEntities = state => state.entities.modals;

const getLoggedInUser = state => state.user;
export const isUserLogged = state => state.user.token !== null;
export const getUserInfo = createSelector([getLoggedInUser], (user) => user ? user.info : null);
export const getUserId = createSelector([getUserInfo], (userInfo) => userInfo ? userInfo.id : null);
export const getUserToken = state => state.user.token;
export const isUserIE = state => { const info = getUserInfo(state); return info && info.iecode; };
export const getUserIePoints = state => state.checkoutData.points;
export const getIeCodeResultEmailSent = state => state.checkoutData.ui.ieCodePanel.emailSent;
export const isIeCodePanelVisible = state => state.checkoutData.ui.ieCodePanel.visible;
export const getIeCodeValidateCardNumber = state => state.ui.iePanel.cardNumber;
export const getIeNewNip = state => state.ui.iePanel.newNip;
export const getUserPurchasesByType = (state, type) => state.user.purchases[type];
export const getUserPurchases = state => getUserPurchasesByType(state, 'purchases') || null;
export const getUserCandybarPurchases = state => getUserPurchasesByType(state, 'candybar') || null;
export const getUserPurchasesFutureOnly = createSelector(
  [getUserPurchases],
  (purchases) => {
    const now = (new Date()).getTime() / 1000;
    const timeWindow = 2 * 60 * 60;
    const limit = now - timeWindow;

    if (!purchases) {
      return [];
    }

    return purchases.filter(purchase => {
      return purchase.session.date > timeWindow;
    });
  }
);
export const isUserPurchasesLoading = state => state.user.purchases.purchasesLoading;
export const isUserCandybarPurchasesLoading = state => state.user.purchases.candybarLoading;
export const getUserSessions = state => state.user.sessions.sessions;
export const isUserSessionsLoading = state => state.user.sessions.loading;
export const isUserSessionBeingKilled = state => state.user.sessions.killing;
export const isUserStuffProcessing = state => {
  for (let i in state.user.isProcessing) {
    if (state.user.isProcessing[i]) {
      return true;
    }
  }
  return false;
};
export const getIeCardData = state => state.user.ieCardData;

export const getSelectedCinemaId = state => state.user.preferences.cinemaId;
export const getSelectedCinema = createSelector([getCinemaEntities, getSelectedCinemaId], (cinemas, selectedCinemaId) => cinemas[selectedCinemaId] || null);
export const getSelectedAreaId = state => state.user.preferences.areaId;
export const getSelectedArea = state => state.user.preferences.areaId && state.entities.areas[state.user.preferences.areaId];
export const getLandingSelectedArea = state => {
  if (!state.ui.landingSelectedArea) return null;
  if (state.ui.landingSelectedArea.indexOf('state-') === 0) {
    return {
      type: 'state',
      entity: state.entities.states[state.ui.landingSelectedArea.split('-').pop()],
    };
  } else {
    return {
      type: 'area',
      entity: state.entities.areas[state.ui.landingSelectedArea],
    }
  }
};

export const getOtherMainAreaMovies = state => {
  const {movies} = state.entities;
  const cinema = getSelectedCinema(state);

  if (!cinema) return undefined;

  const areaId = cinema.area.id;
  const ids = state.home.areaMovies[areaId];
  const inCinemaIds = state.home.billboardMovies[cinema.id] || [];
  const result = [];

  if (!movies || !ids || !ids.length) {
    return [];
  } else {
    ids.forEach(id => movies[id] && inCinemaIds.indexOf(id) === -1 && result.push(movies[id]));
  }

  return result;
};

export const isMainBillboardLoading = state => { // @todo Decide whether it is better to use this method or areBillboardMoviesLoading.
  const cinemaId = getSelectedCinemaId(state);
  return cinemaId && state.home.billboardMovies[cinemaId] === undefined;
};
export const getMainBillboardMoviesWithoutSearchFilter = state => {
  const {movies} = state.entities;
  const ids = state.home.defaultBillboardMovies;
  const result = [];

  if (!movies || ids === undefined) {
    return [];
  } else {
    ids.forEach(id => movies[id] && result.push(movies[id]));
  }

  return result;
};

export const getMainBillboardMovies = state => {
  return filterBySearchTerm(state, getMainBillboardMoviesWithoutSearchFilter(state));
};

export const getExclusiveMovies = state => getMoviesFilteredByType(state, 'exclusive') || [];
export const getPopularMovies = state => {
  const {movies} = state.entities;
  const cinemaId = getSelectedCinemaId(state);
  const ids = cinemaId ? state.home.billboardPopularMovies[cinemaId] : [];
  let result = [];

  if (!movies || ids === undefined) {
    return [];
  } else {
    ids.forEach(id => movies[id] && result.push(movies[id]));
  }

  result = result.slice(0, 5);

  return filterBySearchTerm(state, result);
};

export const getPreSaleMovies = state => getMoviesFilteredByType(state, 'presale') || [];
export const getPremiereMovies = state => getMainBillboardMovies(state).filter(movie => movie.type.indexOf('premiere') > -1) || [];
export const getUpcomingMovies = state => filterBySearchTerm(state, Object.values(state.entities.moviesUpcoming)).sort(sortUpcomingMovies) || [];
const getMoviesFilteredByAttribute = (state, attribute) => getMainBillboardMovies(state).filter(movie => movie.attributes.indexOf(attribute) > -1);
const getMoviesFilteredByType = (state, type) => getMainBillboardMovies(state).filter(movie => movie.type.indexOf(type) > -1);

const getCinemasBillboards = state => state.cinemasBillboards;
const getSelectedCinemaBillboardFull = createSelector([getSelectedCinemaId, getCinemasBillboards], (cinemaId, cinemasBillboards) => {
  if (!cinemaId || !cinemasBillboards[cinemaId]) {
    return null;
  }
  return cinemasBillboards[cinemaId];
});
export const getFeaturedMovies = createSelector([getMainBillboardMoviesWithoutSearchFilter], (movies) => {
  if (!movies) return [];

  movies = movies.filter(movie => movie.featured);
  
  if (!movies.length) {
    return [];
  }

  return movies;
});

const filterBySearchTerm = (state, movies) => {
  const searchTerms = getHeaderSearchTerm(state);

  if (searchTerms) {
    return movies.filter(movie => (
        compareIgnoringAccents(movie.name, searchTerms)
        || compareIgnoringAccents(movie.info.original_title, searchTerms)
        || compareIgnoringAccents(movie.info.director, searchTerms)
        || compareIgnoringAccents(movie.info.cast, searchTerms)
    ));
  } else {
    return movies;
  }
};

const getMoviePopularity = (state, movieId, cinemaId) => state.home.billboardMoviesPopularity[cinemaId][movieId];

const sortUpcomingMovies = (a, b) => {
  const dateA = a.release_date;
  const dateB = b.release_date;

  if (dateA > dateB) {
    return 1;
  } else if (dateA < dateB) {
    return -1;
  } else {
    return 0;
  }
};

export function getAppConfig(state) {
  return state.config;
}
export function getTicketsCheckoutFirstStepConfig(state) {
  return 'seats';
}
export const isShowtimeConfirmationPopupEnabled = createSelector([getAppConfig], config => !!(config && config.showtime_confirmation_popup));
export const getViewText = (state, id) => {
  const config = getAppConfig(state);
  return (('view_texts' in config) && (id in config.view_texts)) ? config.view_texts[id] : undefined;
};

export const getMovie = (state, movieId) => state.entities.movies[movieId];
export const getMovieUpcoming = (state, movieId) => state.entities.moviesUpcoming[movieId];

export const getPromosPromos = state => Object.values(state.entities.promosPromos).sort(sortByProperty('order')) || [];
export const getPromosA = state => Object.values(state.entities.promosA) || [];
export const getPromosFeatured = state => Object.values(state.entities.promosFeatured) || [];
export const getPromosFeaturedMovie = state => Object.values(state.entities.promosFeaturedMovie) || [];
export const getPromosIe = state => Object.values(state.entities.promosIe) || [];
export const getPromosWide = state => Object.values(state.entities.promosWide);
export const getPromosWideFixed = state => Object.values(state.entities.promosWideFixed);
export const isPromosLoading = state => state.promos.isLoading;
export const getPromosLastLoadTime = state => state.promos.lastLoadTime;

export const getLanding = (state, slug) => (slug in state.entities.landings) ? state.entities.landings[slug] : null;

// export const isLandingLoading = state => state.landings.isLoading;
export const isLandingLoading = state => false;
export const getLandingLastLoadTime = state => state.landings.lastLoadTime;
export const getLandingShowtimesData = (state, pageId, area, movieId) => {
  const id = `${pageId}-${area.type}-${area.entity.id}-${movieId}`;
  const {showtimes} = state.landings;
  if (!showtimes || !(id in showtimes)) return null;
  return showtimes[id];
};

export const getCheckoutTimedOut = state => state.checkoutData.timedOut;
export const getCheckoutSession = createSelector([state => state.checkoutData.data], data => data);
export const getCheckoutSeatsLayout = state => state.checkoutData.layout;
export const getCheckoutSelectedSeats = state => state.checkoutData.seats;
export const getCheckoutSeatsToSelect = state => state.checkoutData.seatsCount;
export const areCheckoutSeatsPreSelected = state => state.checkoutData.seatsPreSelected;
export const getCheckoutTransactionId = state => state.checkoutData.order.transactionId;
export const getCheckoutCinema = createSelector(
  [getCheckoutSession, getCinemaEntities],
  (session, cinemas) => {
    return !!session && session.cinema_id ? cinemas[session.cinema_id] : null;
  }
);
export const getMovieEntities = state => state.entities.movies;
export const getCheckoutMovie = createSelector([getMovieEntities, getCheckoutSession], (movies, data) => {
  const movieId = data ? data.movie_id : null;
  
  if (movieId === null || !(movieId in movies)) {
    return null;
  }

  return movies[movieId];
});
export const getCheckoutSteps = createSelector(
  [
    getCheckoutContext,
    getCheckoutSession,
    getUserIePoints,
    getTicketsCheckoutFirstStepConfig
  ],
  (context, session, points, firstStep) => {
    const steps = [];

    if (CHECKOUT_CONTEXT_TICKETS === context) {
      if (session) {
        if ('seats' === firstStep && session.seatallocation) {
          steps.push('seats');
          steps.push('tickets');
        } else {
          steps.push('tickets');
          session.seatallocation && steps.push('seats');
        }
        session.candybar && steps.push('candybar');
      }
      
      if (points.length) {
          steps.push('payment-suggestions');
      }  
    } else if (CHECKOUT_CONTEXT_CANDYBAR === context) {
      steps.push('candybar');
    }

    steps.push('order-review');
    steps.push('payment');

    return steps;
  }
);
export const getCheckoutOrderTickets = createSelector([state => state.checkoutData.order.tickets], tickets => tickets);
export const getCheckoutOrderTicketsCount = createSelector([getCheckoutOrderTickets], (tickets) => {
  let total = 0;

  for (let ticketId in tickets) {
    total += tickets[ticketId].qty;
  }
  
  return total;
});
export const getCheckoutOrderTicketsSeatsCount = createSelector([getCheckoutOrderTickets, getTicketsEntities], (orderTickets, allTickets) => {
  let result = 0;

  Object.keys(orderTickets).forEach(ticketId => {
    const ticket = allTickets[ticketId];
    result += orderTickets[ticketId].qty * ticket.tickets_count;
  });

  return result;
});

export const getCheckoutOrderSeatsLabels = createSelector([getCheckoutSession, getCheckoutSeatsLayout, getCheckoutSelectedSeats], (session, layout, seats) => {
  const seatsLabels = [];

  if (!session) return seatsLabels;
  if (!layout || !layout.length || !seats.length) return seatsLabels;

  layout.forEach(row => row.seats.forEach(seat => {
    if (seats.indexOf(seat.id) > -1) {
      seatsLabels.push(row.name + seat.label);
    }
  }));

  return seatsLabels;
});
export const getCheckoutTicketsWithQuantity = createSelector([getCheckoutSession, getCheckoutOrderTickets], (session, orderTickets) => {
  if (!session || !session.tickets) return [];

  const tickets = session.tickets.map(ticket => {
    const result = Object.assign({}, ticket, {
      qty: orderTickets[ticket.id] ? orderTickets[ticket.id].qty : 0,
    });

    //ticketsSum += result.qty;

    return result;
  });

  Object.keys(tickets).forEach(id => {
    const ticket = tickets[id];
    ticket.enabled = (ticket.max > 0) && (getTicketsAffectingTicket(ticket, tickets).length === 0);
  });

  return tickets;
});

const getTicketsAffectingTicket = (ticket, catalog) => {
  const result = [];

  Object.keys(catalog).forEach(id => {
    const item = catalog[id];

    if (item.id === ticket.id) { return; }
    if (!item.qty) { return }

    if (!ticket.combinable || !item.combinable || item.cant_combine_with.indexOf(ticket.name) >= 0 || (ticket.extra.coupon && item.extra.coupon)) {
      result.push(item);
    }
  });

  return result;
};

export const getCheckoutConfirmationData = state => state.checkoutData.confirmation;
export const getCheckoutCandybarData = state => state.checkoutData.candybar;
export const getCandybarCatalogItemIds = state => getCheckoutCandybarData(state).catalogItems;
export const getCandybarFavoriteItemIds = state => getCheckoutCandybarData(state).favorites;
export const getCandybarCatalogEntities = state => state.entities.candybarCategories;
export const getCandybarItemEntities = state => state.entities.candybarItems;
export const getCandybarFavoriteItems = createSelector([getCandybarCatalogItemIds, getCandybarFavoriteItemIds, getCandybarItemEntities], (catalogItemIds, favoriteItemIds, entities) => {
  const matchingItemIds = catalogItemIds.filter(value => favoriteItemIds.includes(value));
  const result = matchingItemIds.map(itemId => Object.assign({}, entities[itemId], {blocks: ['faves']}));

  return result;
});
export const isCandybarFavorite = (state, itemId) => getCheckoutCandybarData(state).favorites.indexOf(itemId) > -1;
export const getCandybarCatalog = createSelector([getCandybarCatalogEntities], categories => {
  const result = Object.values(categories);
  result.sort((a, b) => {
    if (a.position < b.position) return -1;
    if (a.position > b.position) return 1;
    return 0;
  });

  return result;
});
export const getCandybarBlocks = state => state.checkoutData.candybar.blocks;
export const getCandybarSelectedCategoryId = createSelector([getCheckoutCandybarData, getCandybarCatalog], (data, categories) => {
  const { selectedCategoryId } = data;
  if (selectedCategoryId) {
    return selectedCategoryId;
  }
  
  if (categories.length) {
    return categories[0].id;
  }
  
  return null;
})
export const getCandybarSelectedCategory = createSelector([getCandybarSelectedCategoryId, getCandybarFavoriteItems, getCandybarCatalogEntities], (selectedCategoryId, favoriteItems, categories) => {
  if ('faves' === selectedCategoryId) {
    return {
      id: 'faves',
      name: 'Favoritos',
      items: favoriteItems
    }
  }

  if (!selectedCategoryId || !(selectedCategoryId in categories)) {
    return null;
  }

  return categories[selectedCategoryId];
});
export const getCandybarSelectedCategoryItems = state => {
  const category = getCandybarSelectedCategory(state);

  if (!category) {
    return [];
  }

  return category.items;
};
export const getCandybarSelectedItemId = state => state.checkoutData.candybar.selectedItemId;
export const getCandybarSelectedItem = createSelector(
  [getCandybarSelectedItemId, getCandybarItemEntities],
  (id, entities) => {
    if (!id) {
      return null;
    }

    return entities[id];
  }
);

export const getCheckoutOrderCandybar = state => state.checkoutData.order.candybar;
export const getCandybarSelectedUnitId = state => state.checkoutData.candybar.selectedUnitId;
export const getCandybarSelectedUnit = createSelector([getCandybarSelectedItemId, getCandybarSelectedUnitId, getCheckoutOrderCandybar], (itemId, unitId, order) => {
  if (!itemId || !unitId) {
    return null;
  }

  return order[itemId][unitId];
});
export const getCheckoutOrderCandybarItems = createSelector([getCheckoutOrderCandybar, getCandybarItemEntities], (order, catalog) => {
  const orderItems = [];

  Object.keys(order).forEach(itemId => {
    const item = catalog[itemId];

    if (!item) {
      return;
    }

    Object.keys(order[itemId]).forEach(unitId => {
      const unitItem = Object.assign({}, item, {
        unitId,
        blocks: [0],
        unitConfig: order[itemId][unitId],
      })
      orderItems.push(unitItem);
    });
  });

  return orderItems;
});

export const getCheckoutOrderTicketsParam = createSelector([getCheckoutOrderTickets, getTicketsEntities], (orderTickets, allTickets) => {
  const result = [];

  Object.keys(orderTickets).forEach(ticketId => {
    const ticket = allTickets[ticketId];
    result.push({
      type: ticketId,
      price: ticket.price,
      qty: orderTickets[ticketId].qty
    });
  });

  return result;
});

export const getCheckoutOrderTicketsParamForSuggestions = createSelector([getCheckoutOrderTickets, getTicketsEntities], (orderTickets, allTickets) => {
  const result = [];

  Object.keys(orderTickets).forEach(ticketId => {
    const ticket = allTickets[ticketId];
    const extra = ticket.extra;

    if (ticket.tickets_count > 1) {
      extra.tickets_count = ticket.tickets_count;
    }

    result.push({
      type: ticketId,
      price: ticket.price,
      qty: orderTickets[ticketId].qty,
      payment:[{
        id: 'passes',
        multiplier: ticket.pcmultiplier
      }],
      extra,
    });
  });

  return result;
});

export const getCheckoutOrderCandybarParam = state => {
  const result = [];
  const candybar = getCheckoutOrderCandybar(state);
  const catalog = getCandybarItemEntities(state);

  Object.keys(candybar).forEach(itemId => {
    const item = catalog[itemId];
    const orderItem = candybar[itemId];

    Object.keys(orderItem).forEach(unitId => {
      const unit = orderItem[unitId];
      const unitConfig = unit.config;
      const configs = [];

      Object.keys(unitConfig).forEach(configId => {
        if ('comments' === configId) return;

        configs.push({
          id: configId,
          value: unitConfig[configId],
        });
      });

      result.push({
        id: itemId,
        type: item.type,
        price: getCandybarItemPriceWithConfigs(state, item.id, unitConfig),
        configs,
        comments: unitConfig.comments || '',
      });
    });
  });

  return result;
};

export const getCheckoutCandybarOrderTotal = createSelector([getCheckoutOrderCandybar, getCandybarItemEntities], (order, catalog) => {
  if (!order) {
    return 0;
  }

  let total = 0;

  Object.keys(order).forEach(itemId => {
    const item = catalog[itemId];
    const orderItem = order[itemId];
    Object.keys(orderItem).forEach(unitId => {
      const unit = orderItem[unitId];
      total += getCandybarItemPriceWithSelectedOptions(item, unit.config);
    });
  });

  return total;
});

export const getCheckoutFlowDirection = state => state.checkoutData.flowDirection;
export const getCheckoutPaymentConstraints = state => state.checkoutData.paymentConstraints;
export const getCheckoutPaymentSuggestions = state => state.checkoutData.paymentSuggestions;
export const getCheckoutDefaultPaymentOption = state => {
  const result = {
    tickets: getCheckoutOrderTicketsParam(state),
    candybar: getCheckoutOrderCandybarParam(state),
  };
  const ticketsCount = getCheckoutOrderTicketsCount(state);
  const feePerTicket = ticketsCount ? (getCheckoutBookingFee(state) / ticketsCount) : 0;

  Object.keys(result).forEach(typeId => result[typeId].forEach(item => {
    let price;

    if (typeId === 'tickets') {
      price = (item.price + feePerTicket) * item.qty;
    } else if ('qty' in item) {
      price = item.price * item.qty;
    } else {
      price = getCandybarItemPriceWithConfigs(state, item.id, item.configs);
    }

    item.payment = {
      'id': 'cash',
      'value': price,
    };

    const ticket = state.entities.tickets[item.type];
    if (ticket) {
      if ('coupon_code' in ticket.extra) {
        item.payment.coupon = ticket.extra.coupon_code;
      } else if ('coupon' in ticket.extra) {
        item.payment.coupon = ticket.extra.coupon;
      }
    }
  }));

  return result;
};
export const getCheckoutSelectedPaymentOption = createSelector([
  getCheckoutPaymentSuggestions,
  getCheckoutDefaultPaymentOption
], (paymentSuggestions, defaultPaymentOption) => {
  const {options, selected} = paymentSuggestions;
  return options[selected] || defaultPaymentOption;
});

export const getCheckoutMasterpassData = state => state.checkoutData.masterpass.data;
export const getCheckoutStep = state => state.checkoutData ? state.checkoutData.step : null;
export const isCheckoutCCRequired = state => {
  const amountByMethod = parseOrderAmountByPaymentMethod(getCheckoutSelectedPaymentOption(state));
  return 'cash' in amountByMethod && amountByMethod.cash > 0;
};
export const isCheckoutIeNipRequired = state => {
  const amountByMethod = parseOrderAmountByPaymentMethod(getCheckoutSelectedPaymentOption(state));
  return ('ie' in amountByMethod && amountByMethod.ie > 0)
      || ('passes' in amountByMethod && amountByMethod.passes > 0);
};
export const isPaymentSuggestionNeeded = createSelector([
  getCheckoutPaymentConstraints,
  getUserIePoints,
  getCheckoutSession,
  getCheckoutOrderTickets,
  getTicketsEntities
], (
  paymentConstraints,
  points,
  session,
  ticketsOrder,
  tickets
) => {
  if (paymentConstraints.length && ('card_brand' in paymentConstraints[0])) return false;
  
  const canUsePoints = points && points.length && getCashPaymentMethodsAllowed(tickets, ticketsOrder).join(',') !== 'masterpass';
  let ticketsTotal = 0;
  let usingPasses = false;

  if (!session || !session.tickets) return false;

  session.tickets.forEach(ticket => {
    const qty = ticketsOrder[ticket.id] ? ticketsOrder[ticket.id].qty : 0;

    ticketsTotal += qty * ticket.price;
    if (qty && ticket.extra.is_pass) { usingPasses = true; }
  });

  return (canUsePoints && ticketsTotal > 0) || usingPasses;
});
export const isPaymentWithPointsBlocked = state => {
  let result = false;
  state.checkoutData.points && state.checkoutData.points.forEach(p => {
    if (p.status !== 'available') result = true;
  });
  return result;
}
export const isOrderCostZero = state => {
  const amountByMethod = parseOrderAmountByPaymentMethod(getCheckoutSelectedPaymentOption(state));
  let total = 0;
  Object.keys(amountByMethod).forEach(method => total += amountByMethod[method]);
  return total === 0;
};
export const getCheckoutT1SessionId = state => state.checkoutData.t1.sessionId || null;
export const getCheckoutPaymentMethod = state => state.checkoutData.paymentMethod;
export const getCheckoutPaymentMethods = state => state.checkoutData.paymentMethods;
export const getCheckoutPaymentPostError = state => state.checkoutData.payment.postError;
export const getCheckoutNipCode = state => state.checkoutData.ieNip.nip;
export const getSmartIframeUrl = state => state.checkoutData.smart.url;
export const getStripeRedirectUrl = state => state.checkoutData.stripe.url;
export const getStripePaymentIntentClientSecret = state => state.checkoutData.stripe.clientSecret;
export const getStripePaymentIntentTransactionId = state => state.checkoutData.stripe.transactionId;
export const getStripePaymentIntentReturnUrl = state => state.checkoutData.stripe.returnUrl;
export const getWalletUserId = state => state.wallet.uid;
export const getWalletCards = state => state.wallet.data;
export const getWalletStep = state => state.wallet.step;
export const getWalletAddCardInfo = state => state.wallet.addCardInfo;
export const getCheckoutWalletSelectedCard = state => state.wallet.selectedCard;
export const isWalletProcessing = state => state.wallet.isProcessing;
export const isCheckoutTermsChecked = state => state.checkoutData.termsCheck;
export const isPayPalSingleClickAvailable = state => state.checkoutData.payPalSingleClick;
export const getPayPalClientMetadataId = state => state.checkoutData.payPalClientMetadataId;
export const getCheckoutPaymentFormParams = state => state.checkoutData.payment.formData;

export const isUiMovieTabOpen = state => !state.movieTab.hidden;
export const isUiAreaSelectorOpen = state => state.ui.areaSelectorOpen;
export const isUiCinemaSelectorOpen = state => state.ui.cinemaSelectorOpen;
export const getUiAreaSelectorOptions = state => state.ui.areaSelectorOptions;
export const isUiPostRegisterPopupOpen = state => state.ui.postRegisterPopup && isUserLogged(state) && !isUserIE(state);
export const getPostPurchaseIePopupData = state => state.ui.postPurchaseIePopup;
export const isUiAuthPanelProcessing = state => state.ui.authPanel.login.isProcessing
    || state.ui.authPanel.recoverPass.isProcessing
    || state.ui.authPanel.resetPass.isProcessing
    || state.ui.authPanel.register.isProcessing
;
export const isUiAuthPanelRecoverPassSent = state => state.ui.authPanel.recoverPass.sent;
export const isUiAuthPanelResetPassSent = state => state.ui.authPanel.resetPass.sent;

export const getCinemas = createSelector([getCinemasById], (cinemas) => Object.values(cinemas));
export const getCinema = (state, cinemaId) => getCinemasById(state)[cinemaId];
export const getCinemasByArea = (state, areaId) => Object.values(state.entities.cinemas).filter(cinema => cinema.area.id == areaId);
export const getCinemasByState = (state, stateId) => Object.values(state.entities.cinemas).filter(cinema => cinema.state.id == stateId);
export const getCinemaBillboardDates = (state, cinemaId) => {
  const bb = state.cinemasBillboards[cinemaId];
  if (!bb) {
    return null;
  }

  return bb.reduce((carry, movie) => carry.concat(movie.versions.reduce((carry, version) => carry.concat(version.sessions.map(session => session.datetime.substr(0, 10))), [])), []).filter(unique).sort();
};

export const getCinemaBillboardFull = (state, cinemaId) => state.cinemasBillboards[cinemaId];
export const getCinemaBillboardForDate = (state, cinemaId, date) => getCinemaBillboardWithFilters(state, cinemaId, {date});
export const getCinemaBillboardWithFilters = (state, cinemaId, filters) => {
  let movies = getCinemaBillboardFull(state, cinemaId);
  const result = [];

  if (!movies) { return movies; }

  if (filters.movie && filters.movie.length) {
    movies = movies.filter(movie => filters.movie.indexOf(movie.id) > -1);
  }

  movies.forEach(movie => {
    let versions = filterAvailableVersions(movie.versions);

    if (versions.length && filters.date) {
      versions = filterVersionsForDate(versions, filters.date);
    }

    if (versions.length && filters.format) {
      versions = getFilteredVersions(filters.format, versions);
    }

    if (versions.length && filters.lang) {
      versions = getFilteredVersions(filters.lang, versions);
    }

    if (versions.length && filters.hours) {
      versions = filterVersionsByHours(versions, filters.hours.min, filters.hours.max);
    }

    result.push(Object.assign({}, movie, {
      versions
    }));
  });

  return result.filter(movie => movie.versions.length);
};

export const getStateBillboardDates = (state, stateId) => state.statesBillboards.dates[stateId] || [];
export const getStateBillboardFull = (state, stateId, date) => state.statesBillboards.data[stateId + '_' + date];
export const getStateBillboardWithFilters = (state, stateId, filters) => getZoneBillboardWithFilters(state, getStateBillboardFull(state, stateId, filters.date), filters);
export const getAreaBillboardDates = (state, areaId) => state.areasBillboards.dates[areaId] || [];
export const getAreaBillboardFull = (state, areaId, date) => state.areasBillboards.data[areaId + '_' + date];
export const getAreaBillboardWithFilters = (state, areaId, filters) => getZoneBillboardWithFilters(state, getAreaBillboardFull(state, areaId, filters.date), filters);
export const getZoneBillboardWithFilters = (state, cinemas, filters) => {
  const result = [];

  if (!cinemas) { return cinemas; }

  if (filters.cinema && filters.cinema.length) {
    cinemas = cinemas.filter(cinema => filters.cinema.indexOf(cinema.id) > -1);
  }

  cinemas.forEach(cinema => {
    let movies = [];
    let cinemaMovies = cinema.movies;

    if (filters.movie && filters.movie.length) {
      cinemaMovies = cinemaMovies.filter(movie => filters.movie.indexOf(movie.id) > -1);
    }

    cinemaMovies.forEach(movie => {
      let versions = filterAvailableVersions(movie.versions);

      if (!versions.length) {
        return;
      }

      if (filters.date) {
        versions = filterVersionsForDate(versions, filters.date);
      }

      if (filters.format && filters.format.length) {
        versions = getFilteredVersions(filters.format, versions);
      }

      if (filters.lang && filters.lang.length) {
        versions = getFilteredVersions(filters.lang, versions);
      }

      if (filters.hours) {
        versions = filterVersionsByHours(versions, filters.hours.min, filters.hours.max);
      }

      versions.length && movies.push(Object.assign({}, movie, {
        versions
      }));
    });

    movies.length && result.push(Object.assign({}, cinema, {
      movies
    }));
  });

  return result;
};
export const isBillboardLoading = (state, areaType, areaId, date) => state[areaType + 'sBillboards'].isLoading[areaId + '_' + (date || 'initial')];
export const getBillboardLastLoadTime = (state, areaType, areaId, date) => state[areaType + 'sBillboards'].lastLoadTime[areaId + '_' + (date || 'initial')];

export const getState = (state, stateId) => state.entities.states[stateId];

export const getArea = (state, areaId) => state.entities.areas[areaId];
export const getAreas = (state) => Object.values(state.entities.areas);
export const getAreasById = (state) => state.entities.areas;
export const getStatesById = (state) => state.entities.states;
export const getStates = createSelector([getStatesById], (states) => {
  const result = [];
  const existingIds = Object.keys(states);
  const fixedOrder = ["8","16","14"];

  fixedOrder.forEach(id => existingIds.indexOf(id) > -1 && result.push(states[id]));

  return result.concat(
      Object.values(states).filter(state => fixedOrder.indexOf(state.id.toString()) === -1)
          .sort(sortByProperty('name'))
  );
});
export const getStatesWithCinemas = createSelector([getStates, getCinemas], (states, cinemas) => {
  const areasWithCinemas = cinemas.map(cinema => cinema.area.id);
  const result = states.filter(state => state.areas.some(area => areasWithCinemas.indexOf(area.id) > -1));

  return result;
});

export const getBootstrapConfig = state => state.config;
export const getBootstrapFilters = createSelector([getBootstrapConfig], (config) => {
  const attributes = {};

  if (!config || !config.attributes) {
    return attributes;
  }

  for (let key in config.attributes.movies) {
    const attribute = config.attributes.movies[key];
    if (attribute.filter) attributes[key] = attribute;
  }

  attributes['2d'] = {display_name: 'Tradicional'};

  return attributes;
});

export const getModals = createSelector(getModalsEntities, modals => modals ? Object.values(modals) : null);

export const getHeaderSearchTerm = state => state.home.searchTerm;

export const isContactFormProcessing = state => state.contactForm.isProcessing;
export const isContactFormSubmitted = state => state.contactForm.isSubmitted;

export const isJobsFormProcessing = state => state.jobsForm.isProcessing;
export const isJobsFormSubmitted = state => state.jobsForm.isSubmitted;

export const isArcoFormProcessing = state => state.arcoForm.isProcessing;
export const isArcoFormSubmitted = state => state.arcoForm.isSubmitted;
export const getArcoFormRef = state => state.arcoForm.ref;

export const isCorpSalesFormProcessing = state => state.corpSalesForm.isProcessing;
export const isCorpSalesFormSubmitted = state => state.corpSalesForm.isSubmitted;

export const isIeSignUpFormProcessing = state => state.ie.signUpFormProcessing;
export const isIeSignUpCompleted = state => state.ie.signUpFormCompleted;
export const isIeSignUpSubmitted = state => state.ie.signUpFormSubmitted;
export const isIeBenefitsLoading = state => state.ieBenefits.isLoading;
export const getIeBenefitsLastLoadTime = state => state.ieBenefits.lastLoadTime;
export const getIeBenefitsByCategory = state => {
  const result = {};
  const {categories} = state.ieBenefits;

  Object.keys(categories).forEach(category => {
    result[category] = categories[category].map(id => getIeBenefit(state, id));
  });

  return result;
};
export const getIeBenefit = (state, id) => state.entities.ieBenefits[id];

export const getNowSessions = (state, cinemaId) => state.now.sessions[cinemaId] || null;

export const isNewsLoading = state => state.news.isLoading;
export const getNewsLastLoadTime = state => state.news.lastLoadTime;
export const getNews = state => state.news.ids ? state.news.ids.map(id => state.entities.news[id]) : null;
export const getNewsSingle = (state, articleId) => state.entities.news[articleId] || null;

export const getPost = (state, slug) => state.entities.posts[slug];
export const isPostLoading = (state, slug) => state.posts.isLoading[slug] || false;
export const getPostLastLoadTime = (state, slug) => state.posts.lastLoadTime[slug] || false;

export const getCheckoutBookingFee = state => state.checkoutData.bookingFee;
export const getCheckoutSelectedPaymentOptionBookingFee = state => {
  const fallback = state.checkoutData.bookingFee;
  return fallback;
  const {paymentSuggestions: {selected, options} = {selected: null, options: []}} = state.checkoutData;
  if (!selected || !(selected in options)) return fallback;
  
  return options[selected].booking_fee;
};
export const getCheckoutNonRedeemableTickets = state => state.checkoutData.paymentSuggestions.nonRedeemableTickets.map(item => state.entities.tickets[item]);

export const getFingerprint = state => state.fingerprint;
export const getLoginEmail = state => state.auth.email;
export const getLoginPassword = state => state.auth.password;

export const getSignUpManualStep = state => !state.auth.preFillInfo.token && !state.auth.preFillInfo['sign-up-options'] ? '1' : '2';
export const getSignUpManualPreFillInfo = state => state.auth.preFillInfo;

export const getUserProfileEdit = state => state.ui.userProfileEdit;

export const getNotificationsMovieUpcomingSubscriptions = state => state.notifications.upcomingMovies;
export const getNotificationRequestCallback = state => state.notifications.callback;
export const getNotificationsPermission = state => state.notifications.permission;

export const isRefundRequestProcessing = state => state.refundRequest.isProcessing;
export const getRefundRequestStep = state => state.refundRequest.step;
export const getRefundRequestCodes = state => state.refundRequest.codes;
export const getRefundRequestProcessedData = state => state.refundRequest.processedData;
export const getRefundRequestTransaction = state => state.refundRequest.transaction;

export const getUiCustomLoadingAnimation = state => state.ui.customLoadingAnimation;
export const isAuthPinDialogOpen = state => state.ui.authPanel.pin.visible;
export const getAuthPinDialogContext = state => state.ui.authPanel.pin.context;
export const getAuthPinDialogError = state => state.ui.authPanel.pin.error;
export const getAuthPinDialogMessage = state => state.ui.authPanel.pin.message;
export const getAuthPinDialogOriginalAction = state => state.ui.authPanel.pin.originalAction;
export const getThemeChangeCounter = state => state.ui.themeChangeCounter;


export const getCheckoutSeatTypes = createSelector([getBootstrapConfig, getCheckoutSession], (config, session) => {
  const types = config.seat_types || {};
  const overrides = session.seat_types_override;

  if (!overrides) return types;

  const typesToOverride = Object.keys(overrides);
  const result = {};
  
  Object.keys(types).forEach(k => {
    if (typesToOverride.indexOf(k) >= 0) {
      result[k] = overrides[k];
    } else {
      result[k] = types[k];
    }
  });

  return result;
});

export const getIeSignUpData = state => state.ie.signUpOptions || {};
export const getIeSignUpOptions = state => (getIeSignUpData(state)).options || [];
export const getIeSignUpPaymentGateway = state => (getIeSignUpData(state)).payment_gateway;
export const getIeSignUpResult = state => state.checkoutData.paymentNew.successData[CHECKOUT_CONTEXT_LOYALTY] || null;
export const getIePaymentUrl = state => state.ie.paymentUrl;
export const getIeSignUpUserInfo = state => state.ie.userInfo;

export const getUserSignUpPaymentResult = state => state.checkoutData.paymentNew.successData[CHECKOUT_CONTEXT_SIGN_UP] || null;
export const isUserSignUpPaymentProcessing = (state) => state.checkoutData.paymentNew.isProcessing.includes(CHECKOUT_CONTEXT_SIGN_UP);
export const isLoyaltyPaymentProcessing = (state) => state.checkoutData.paymentNew.isProcessing.includes(CHECKOUT_CONTEXT_LOYALTY);

export const getGiftCardPurchasePaymentResult = state => state.checkoutData.paymentNew.successData[CHECKOUT_CONTEXT_GIFT_CARD] || null;
export const isGiftCardPaymentProcessing = state => state.checkoutData.paymentNew.isProcessing.includes(CHECKOUT_CONTEXT_GIFT_CARD);

export const isSomethingLoading = state => state.ui.authPanel.login.isProcessing
    || state.checkoutData.isLoading
    || state.checkoutData.ticketsSelection.isProcessing
    || state.checkoutData.seatsSelection.isProcessing
    || state.checkoutData.masterpass.isProcessing
    || state.checkoutData.smart.isProcessing
    || state.checkoutData.stripe.isProcessing
    || state.checkoutData.ieNip.isValidating
    || state.checkoutData.paymentSuggestions.isLoading
    || state.checkoutData.candybar.isLoading
    || state.checkoutData.ui.ieCodePanel.processing
    || state.checkoutData.ui.promoCodesPanel.processing
    || state.wallet.isProcessing
    || isNewsLoading(state)
    || isPromosLoading(state)
    || isUserSessionBeingKilled(state)
    || isUserStuffProcessing(state)
    || isIeSignUpFormProcessing(state)
    || state.ie.signUpOptionsLoading
    || state.auth.isProcessing
    || isUiAuthPanelProcessing(state)
    || isUserCouponsLoading(state)
    || isUserCouponsProcessing(state)
    || isUserCandybarPurchasesLoading(state)
    || isWalletCardRemoving(state)
    || isWalletCardAdding(state)
    || isWalletCardPinRequesting(state)
;

export const getCheckoutPaymentEndpoint = state => state.checkoutData.paymentEndpoint || 'buy/complete';

export const getCheckoutLoyaltyProgram = state => state.checkoutData.loyaltyProgram || {};

export function getCheckoutContext(state) {
  const data = state.checkoutData;
  const context = data.context;

  return context;
}

export const getCheckoutSelectedTicketsString = createSelector([getCheckoutTicketsWithQuantity], (tickets) => {
  const items = [];

  if (!tickets) {
    return '';
  }

  tickets.forEach(ticket => {
    ticket.qty && items.push(ticket.qty + ' ' + ticket.name);
  });

  return items.join(' + ');
});
export const getCheckoutTicketsTotal = createSelector([getCheckoutTicketsWithQuantity], (tickets) => {
  return tickets.reduce((carry, ticket) => carry + (ticket.qty * ticket.price), 0);
});
export const getCheckoutGrandTotal = createSelector([
  getCheckoutTicketsTotal,
  getCheckoutCandybarOrderTotal,
  getCheckoutSelectedPaymentOptionBookingFee
], (ticketsTotal, candybarTotal, bookingFee) => {
  return ticketsTotal + candybarTotal + bookingFee;
});
export const getCheckoutSelectedMethodGrandTotalString = createSelector([
  getCheckoutGrandTotal,
  getCheckoutSelectedPaymentOption
], (
  grandTotal,
  paymentOption
) => {
  if (paymentOption) {
    return getOptionLabel(paymentOption);
  }

  if (grandTotal) {
    return centsToCurrencyString(grandTotal);
  }

  return '';
});

export const shouldCheckoutRenderWallet = createSelector(
  [getCheckoutStep, getCheckoutPaymentMethod],
  (step, paymentMethod) => {
    return step === 'payment' && paymentMethod === 'credit-card';
  }
);
export const getCheckoutLoyaltyProgramSubscriptionId = state => getCheckoutLoyaltyProgram(state).subscriptionId;
export const getCheckoutLoyaltyProgramSubscriptionPrice = state => getCheckoutLoyaltyProgram(state).price;
export const getCheckoutLoyaltyProgramUserForm = state => getCheckoutLoyaltyProgram(state).userForm;
export const getCheckoutLoyaltyProgramStripeInfo = state => getCheckoutLoyaltyProgram(state).stripe;
export const getCheckoutLoyaltyProgramType = state => getCheckoutLoyaltyProgram(state).type;
export const isCheckoutProcessing = state => state.checkoutData.payment.isProcessing || state.checkoutData.paymentNew.isProcessing.length > 0;

export const getSnapchatConfig = state => state.config.snap || {};
export const isSnapchatEnabled = state => getSnapchatConfig(state).active;
export const getSnapchatApiToken = state => getSnapchatConfig(state).token ? getSnapchatConfig(state).token.trim() : null;
export const getSnapchatLenses = state => getSnapchatConfig(state).lenses || [];

// Packages selectors
export const getPackages = (state) => state.packages.packages;
export const getPackagesError = (state) => state.packages.error;
export const getPackagePurchaseError = (state) => state.packages.purchaseError;
export const getPackagePurchaseSuccess = (state) => state.checkoutData.paymentNew.successData[CHECKOUT_CONTEXT_LOYALTY_PACKAGES] || null;
export const isPackagesLoading = (state) => state.packages.isLoading;
export const isPackagePurchaseProcessing = (state) => state.checkoutData.paymentNew.isProcessing.includes(CHECKOUT_CONTEXT_LOYALTY_PACKAGES);

export const getCheckoutPaymentGateway = state => state.checkoutData.paymentGateway || getAppConfig(state).default_payment_gateway;

export const isCheckoutPayButtonVisible = createSelector(
  [shouldCheckoutRenderWallet, getCheckoutPaymentGateway],
  (shouldRenderWallet, paymentGateway) => {
    return shouldRenderWallet && paymentGateway !== 'smart' && paymentGateway !== 'stripe-elements';
  }
);
export const isCheckoutPayButtonDisabled = createSelector(
  [shouldCheckoutRenderWallet, getWalletAddCardInfo],
  (shouldRenderWallet, addCardInfo) => {
    return !(shouldRenderWallet && addCardInfo.valid);
  }
);

export const getCheckoutNextStep = createSelector([getCheckoutStep, getCheckoutSteps], (step, steps) => {
  const i = steps.indexOf(step);

  if (i >= steps.length) {
    return null;
  }
  
  return steps[i + 1];
});

export const getCheckoutPrevStep = createSelector([getCheckoutStep, getCheckoutSteps], (step, steps) => {
  const i = steps.indexOf(step);

  if (i < 1) {
    return null;
  }
  
  return steps[i - 1];
});

export const getCheckoutNextButtonText = createSelector([
  getCheckoutNextStep,
  getCheckoutSession,
  getCheckoutOrderCandybar,
  getCheckoutStep
], (nextStep, session, candybarOrder, currentStep) => {
  switch (nextStep) {
    case CHECKOUT_STEPS.TICKETS:
      return 'Ir a selección de boletos';
    case CHECKOUT_STEPS.SEATS:
      return 'Ir a selección de asientos';
    case CHECKOUT_STEPS.ORDER_REVIEW:
      return 'Ir a resumen de compra';
    case CHECKOUT_STEPS.CANDYBAR:
      return 'Ir a dulcería';
    case CHECKOUT_STEPS.PAYMENT_SUGGESTIONS:
      return (!session.candybar || Object.keys(candybarOrder).length > 0) ? 'Ir a forma de pago' : 'Saltear este paso'
    case CHECKOUT_STEPS.PAYMENT:
      return 'Ir a pago';
    case CHECKOUT_STEPS.CONFIRMATION:
      return 'Finalizar';
  }

  switch (currentStep) {
    case CHECKOUT_STEPS.ORDER_REVIEW:
      return 'Ir a pago';
  }

  return '';
});

export const isCheckoutNextButtonEnabled = createSelector(
  [
    getCheckoutStep,
    getTicketsCheckoutFirstStepConfig,
    getCheckoutOrderTicketsSeatsCount,
    getCheckoutOrderTicketsCount,
    getCheckoutOrderSeatsLabels
  ],
  (
    currentStep,
    firstStepConfig,
    ticketsSeatsCount,
    ticketsSum,
    seatsLabels
  ) => {
    let enabled = true;
    const isFirstStep = firstStepConfig === currentStep;

    if (CHECKOUT_STEPS.TICKETS === currentStep) {
        return isFirstStep ? ticketsSum > 0 : ticketsSeatsCount === seatsLabels.length;
    }
    if (CHECKOUT_STEPS.SEATS === currentStep) {
        return !!seatsLabels && seatsLabels.length > 0;
    }

    return enabled;
  }
);